import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Box, Button, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { keyframes } from '@mui/system';

import legacyTheme from '../../theme';
import { tokens } from '../../theme-next';
import { useAuth } from '../AuthContext';
import { useFormCGIAStore } from '../FormCGIA/store/useFormCGIAStore';
import { seedSessionSnapshot } from '../FormCGIA/v2/hooks/useAutoExtractQueue';
import { useBackgroundUploads } from './BackgroundUploadsProvider';
import type { InFlightUploadRow } from '../../hooks/useInFlightUploads';
import type { V2SessionSnapshot } from '../FormCGIA/store/useFormCGIAStore';

/**
 * Tracker persistant des uploads CG en arrière-plan.
 *
 * Spec UX (issue du cahier des charges) :
 *  - Toast bas-droite, glassmorphism iOS poussé (blur 28 px, saturate,
 *    bordure white translucide, multi-layer shadow, rayon 24 px).
 *  - **Persiste** tant que l'utilisateur ne ferme pas explicitement (pas
 *    d'auto-hide après X secondes). Close manuel uniquement.
 *  - Réductible : on clique sur la bandeau pour replier/déplier. Replié,
 *    le badge avec le nombre reste visible.
 *  - **Multi-uploads** : stack vertical des dossiers en cours. Chaque card
 *    a sa propre progress bar (upload + analyse) et son état lisible.
 *  - **Expandable** : clic sur une card → détail per-fichier (état OCR
 *    de chaque pièce du dossier). Re-clic ferme.
 *  - **"Voir"** : navigue vers /form-cgia pour la session active (résumé
 *    de la session en cours dans le store).
 *  - **"Nouvelle CG"** : reset le store v2 + redirige vers /form-cgia pour
 *    démarrer un nouveau dossier pendant que celui en cours continue.
 *
 * Charte alignée sur PlateAndDepartmentScreen :
 *  - Overline jaune `tokens.color.yellow` 11 px tracking 0.15em
 *  - Titre dark 16 px fontWeight 800
 *  - Cards intérieures avec liseré gauche jaune sur dossier sélectionné
 *  - Boutons inspirés du `dealActionButton` / `secondaryDealActionButton`
 */

const STATE_LABELS: Record<string, { label: string; color: string }> = {
  queued: { label: 'En file', color: tokens.color.textMuted },
  uploading: { label: 'Téléversement', color: tokens.color.blue },
  analyzing: { label: 'Analyse IA', color: tokens.color.yellowDark },
  submitting: { label: 'Finalisation', color: tokens.color.yellowDark },
  done: { label: 'Terminé', color: tokens.color.green },
  cancelled: { label: 'Annulé', color: tokens.color.textMuted },
  error: { label: 'Erreur', color: tokens.color.red ?? '#d04545' },
};

/**
 * Téléversement ET analyse IA à 100 % mais demande pas encore soumise :
 * plus rien n'est "en cours" côté fichiers — il ne reste qu'à soumettre.
 * Le state backend reste 'uploading'/'analyzing' tant que l'agence n'a pas
 * lancé le recap, d'où cette détection par compteurs.
 */
const isAwaitingSubmit = (u: InFlightUploadRow): boolean =>
  (u.state === 'uploading' || u.state === 'analyzing') &&
  u.files_total > 0 &&
  u.files_uploaded >= u.files_total &&
  u.files_analyzed >= u.files_total;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// Pop-scale subtil joué au mount du bloc "Demande finalisée" — célèbre
// la transition uploading → done sans agressivité.
const popIn = keyframes`
  0%   { transform: scale(0.94); opacity: 0; }
  60%  { transform: scale(1.02); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

export const BackgroundUploadsTracker = () => {
  const { uploads, cancelUpload, patchUpload, wsConnected, forceShowTrackerNonce } =
    useBackgroundUploads();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const resetV2 = useFormCGIAStore((s) => s.resetV2);
  const startV2 = useFormCGIAStore((s) => s.startV2);
  const serializeV2Snapshot = useFormCGIAStore((s) => s.serializeV2Snapshot);
  const hydrateV2FromSnapshot = useFormCGIAStore((s) => s.hydrateV2FromSnapshot);
  const activeInFlightId = useFormCGIAStore((s) => s.activeInFlightId);

  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Re-ouvre le tracker quand l'utilisateur clique sur le bloc "Demandes en
  // cours d'analyse" dans AgencyCGList. Le provider incrémente
  // `forceShowTrackerNonce` qui déclenche cet effet et reset `dismissed`.
  useEffect(() => {
    if (forceShowTrackerNonce > 0) {
      setDismissed(false);
      setCollapsed(false);
    }
  }, [forceShowTrackerNonce]);

  // Notification système à la complétion d'un upload — best-effort.
  // On garde un Set des ids déjà notifiés pour éviter de spammer.
  const notifiedDoneIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    // Demande la permission une seule fois (au premier render avec uploads).
    if (Notification.permission === 'default') {
      void Notification.requestPermission().catch(() => {});
    }
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    uploads.forEach((u) => {
      if (u.state === 'done' && !notifiedDoneIdsRef.current.has(u.id)) {
        notifiedDoneIdsRef.current.add(u.id);
        const payload = (u.payload as { regPlate?: string } | null) ?? null;
        try {
          new Notification('Demande finalisée · Skipcar', {
            body: `${payload?.regPlate ?? 'Votre dossier'} — analyse IA terminée, prêt pour vérification.`,
            tag: `skipcar-cg-${u.id}`,
            icon: '/favicon.ico',
          });
        } catch {
          // Notification API peut throw sur Safari mobile, no-op.
        }
      }
    });
  }, [uploads]);

  // Filtre : on garde les actifs et les fraîchement terminés (badge "Terminé").
  // Les "cancelled" sont cachés (l'utilisateur ne veut pas les voir traîner).
  const visibleUploads = uploads.filter((u) => u.state !== 'cancelled');

  // Auto-réouverture si un upload jamais vu apparaît APRÈS un dismiss
  // explicite. Le dismiss vaut seulement pour les uploads qui étaient déjà
  // dans le tracker au moment du clic X — pas pour les futures sessions.
  const seenIdsRef = useRef<Set<string>>(new Set(uploads.map((u) => u.id)));
  useEffect(() => {
    const hasNew = visibleUploads.some((u) => !seenIdsRef.current.has(u.id));
    if (hasNew) {
      visibleUploads.forEach((u) => seenIdsRef.current.add(u.id));
      if (dismissed) setDismissed(false);
    }
  }, [visibleUploads, dismissed]);

  // Délogué : aucun toast d'avancement ne doit rester à l'écran (les uploads en
  // arrière-plan appartiennent à une session authentifiée). Sur logout, le
  // tracker disparaît immédiatement.
  if (!isAuthenticated) return null;

  if (dismissed || visibleUploads.length === 0) return null;

  // Ventilation pour le titre du header : "en cours" = il se passe encore
  // quelque chose côté fichiers ; "à soumettre" = upload + analyse à 100 %
  // mais demande non soumise ; le reste (done) = "terminé".
  const awaitingSubmitCount = visibleUploads.filter(isAwaitingSubmit).length;
  const activeCount =
    visibleUploads.filter(
      (u) => u.state === 'queued' || u.state === 'uploading' || u.state === 'analyzing' || u.state === 'submitting',
    ).length - awaitingSubmitCount;
  const doneCount = visibleUploads.filter((u) => u.state === 'done').length;

  const headerTitle = (() => {
    const parts = [
      activeCount > 0 ? `${activeCount} en cours` : null,
      awaitingSubmitCount > 0 ? `${awaitingSubmitCount} à soumettre` : null,
      doneCount > 0 ? `${doneCount} terminé${doneCount > 1 ? 's' : ''}` : null,
    ].filter((p): p is string => p !== null);
    if (parts.length > 0) return parts.join(' · ');
    return `${visibleUploads.length} dossier${visibleUploads.length > 1 ? 's' : ''} prêt${visibleUploads.length > 1 ? 's' : ''}`;
  })();

  // Verrou navigation pendant la finalisation : tant qu'au moins UNE
  // session est en `submitting`, on bloque "Basculer sur ce dossier" et
  // "Lancer une autre demande". Évite que l'agence parte ailleurs au
  // moment critique de la création Pipedrive deal + Qonto facture.
  const finalizingLock = visibleUploads.some((u) => u.state === 'submitting');

  const handleNewCg = () => {
    // Persiste le snapshot de la session ACTIVE avant de la mettre en pause —
    // sinon un "Voir le dossier" ultérieur sur cette session rechargerait un
    // état tronqué (juste { regPlate, department } posé au startUpload).
    if (activeInFlightId) {
      const snapshot = serializeV2Snapshot();
      void patchUpload(activeInFlightId, { payload: snapshot } as any);
    }
    resetV2();
    startV2();
    navigate('/form-cgia');
  };

  const handleSeeUpload = (upload: InFlightUploadRow) => {
    // Si on est DÉJÀ sur le dossier cliqué, simple navigation (no-op si on
    // est déjà sur /form-cgia).
    if (activeInFlightId === upload.id) {
      navigate('/form-cgia');
      return;
    }
    // Bascule de session : snapshot le store courant dans l'in-flight actif,
    // puis hydrate le store avec le payload du dossier ciblé.
    if (activeInFlightId) {
      const outgoingSnapshot = serializeV2Snapshot();
      void patchUpload(activeInFlightId, { payload: outgoingSnapshot } as any);
    }
    const incomingPayload = upload.payload as Partial<V2SessionSnapshot> | null;
    if (incomingPayload && typeof incomingPayload === 'object') {
      // Cast safe : le store gère les champs manquants avec leurs defaults.
      hydrateV2FromSnapshot(incomingPayload as V2SessionSnapshot, upload.id);
      // Seed le snapshot in-memory : sans ça, un nouveau dépôt après bascule
      // résume des compteurs à zéro et écrase le backend.
      //
      // `identityDocsPool` est INDISPENSABLE ici : depuis d81af1d6, il est la
      // mémoire des pièces d'identité (`uploadedDocs` n'en est plus que la
      // projection). Le seeder sans lui laissait le dossier repris avec un pool
      // vide ; au premier résultat OCR suivant, `applyIdentityRouting` repartait
      // de zéro et effaçait CNI/permis déjà déposés — c'est le « il mélange les
      // pièces entre les deux dossiers » de François (05/08).
      seedSessionSnapshot(upload.id, {
        ingestionItems: incomingPayload.ingestionItems,
        uploadedDocs: incomingPayload.uploadedDocs,
        extractedFields: incomingPayload.extractedFields,
        identityDocsPool: incomingPayload.identityDocsPool,
      });
    }
    navigate('/form-cgia');
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        left: 24,
        zIndex: 1300,
        width: 380,
        maxWidth: 'calc(100vw - 48px)',
        // Glassmorphism iOS poussé : blur 28px + saturate 200%, fond white
        // translucide avec gradient subtil, bordure white 0.4, ombre multi-layer.
        backgroundColor: 'rgba(255, 255, 255, 0.62)',
        backgroundImage:
          'linear-gradient(180deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.0) 100%)',
        backdropFilter: 'blur(28px) saturate(200%)',
        WebkitBackdropFilter: 'blur(28px) saturate(200%)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '24px',
        boxShadow:
          '0 24px 60px -12px rgba(0, 0, 0, 0.22), ' +
          '0 8px 24px -8px rgba(0, 0, 0, 0.12), ' +
          '0 1px 0 rgba(255, 255, 255, 0.6) inset, ' +
          '0 -1px 0 rgba(0, 0, 0, 0.04) inset',
        overflow: 'hidden',
      }}
    >
      {/* ── Header — overline jaune + titre + close ──────────────────── */}
      <Box
        sx={{
          padding: '16px 18px 14px',
          borderBottom: collapsed ? 'none' : '0.5px solid rgba(0, 0, 0, 0.06)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontSize: '10.5px',
                fontWeight: 700,
                color: tokens.color.yellow,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
              }}
            >
              Dossiers en arrière-plan
            </Typography>
            {/* Badge "Hors-ligne" subtil quand WS down — wording adressé à
                l'agence : on insiste que l'analyse continue, seul l'affichage
                est moins réactif. */}
            {!wsConnected && (
              <Tooltip title="Connexion temps réel interrompue — l'affichage se rafraîchit toutes les 30 s. Votre demande continue d'être analysée normalement en arrière-plan.">
                <Typography
                  sx={{
                    fontFamily: 'Urbanist, sans-serif',
                    fontSize: '10px',
                    fontWeight: 800,
                    color: 'rgba(0,0,0,0.55)',
                    backgroundColor: 'rgba(0,0,0,0.06)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Hors-ligne
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: 800,
              color: tokens.color.dark,
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              marginTop: '2px',
            }}
          >
            {headerTitle}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((c) => !c);
          }}
          sx={{ color: tokens.color.textMuted, padding: '2px', marginLeft: '-2px' }}
        >
          {collapsed ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        </IconButton>
        <Tooltip title="Fermer">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
            sx={{ color: tokens.color.textMuted, padding: '2px' }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Body : stack de cards ─────────────────────────────────────── */}
      {!collapsed && (
        <>
          <Stack spacing={1} sx={{ padding: '12px 12px 8px' }}>
            {visibleUploads.map((upload) => {
              const payload = (upload.payload as { regPlate?: string; department?: string } | null) ?? null;
              const regPlate = payload?.regPlate ?? '—';
              const department = payload?.department ?? '';
              const isExpanded = expandedId === upload.id;
              const isDone = upload.state === 'done';
              const isError = upload.state === 'error';
              const isActive =
                upload.state === 'uploading' ||
                upload.state === 'analyzing' ||
                upload.state === 'submitting';

              const total = upload.files_total || 1;
              const uploadPct = Math.min(100, Math.round((upload.files_uploaded / total) * 100));
              const analyzePct = Math.min(100, Math.round((upload.files_analyzed / total) * 100));
              const awaitingSubmit = isAwaitingSubmit(upload);
              const stateInfo = awaitingSubmit
                ? { label: 'Analyse terminée · à soumettre', color: tokens.color.green }
                : (STATE_LABELS[upload.state] ?? STATE_LABELS.uploading);

              return (
                <Box
                  key={upload.id}
                  onClick={() => setExpandedId(isExpanded ? null : upload.id)}
                  sx={{
                    padding: '14px 14px 12px',
                    borderRadius: '14px',
                    // Card "done" : fond vert tendre + bordure verte +
                    // léger inner glow pour célébrer visuellement. Pour
                    // les autres états, on garde le glassmorphism neutre.
                    backgroundColor: isDone
                      ? 'rgba(34, 197, 94, 0.10)'
                      : 'rgba(255, 255, 255, 0.55)',
                    border: `1px solid ${
                      isDone
                        ? tokens.color.greenBorder
                        : isExpanded
                          ? tokens.color.yellow
                          : 'rgba(0, 0, 0, 0.06)'
                    }`,
                    boxShadow: isDone
                      ? `0 0 0 1px ${tokens.color.greenBorder} inset, 0 6px 16px -4px rgba(34, 197, 94, 0.22)`
                      : 'none',
                    cursor: 'pointer',
                    transition: 'all 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                    position: 'relative',
                    // Pop-scale subtil au passage en `done` — animation
                    // CSS `key`-based en haut du composant via key={state}.
                    '&:hover': {
                      backgroundColor: isDone
                        ? 'rgba(34, 197, 94, 0.14)'
                        : 'rgba(255, 255, 255, 0.78)',
                      borderColor: isDone ? tokens.color.green : tokens.color.yellow,
                    },
                  }}
                >
                  {/* Liseré gauche jaune si expanded */}
                  {isExpanded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 12,
                        bottom: 12,
                        width: 3,
                        borderRadius: '0 2px 2px 0',
                        backgroundColor: tokens.color.yellow,
                      }}
                    />
                  )}

                  {/* Header card : plaque + état */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '15px',
                          fontWeight: 800,
                          color: tokens.color.dark,
                          letterSpacing: '0.02em',
                          fontVariantNumeric: 'tabular-nums',
                          lineHeight: 1.2,
                        }}
                      >
                        {regPlate}
                        {department && (
                          <Typography
                            component="span"
                            sx={{ fontSize: '12px', fontWeight: 600, color: tokens.color.textMuted, marginLeft: '6px' }}
                          >
                            · dép. {department}
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          fontWeight: 700,
                          color: stateInfo.color,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          marginTop: '2px',
                        }}
                      >
                        {isDone && <CheckCircleIcon sx={{ fontSize: 12, verticalAlign: 'middle', marginRight: '4px' }} />}
                        {isError && <ErrorOutlineIcon sx={{ fontSize: 12, verticalAlign: 'middle', marginRight: '4px' }} />}
                        {stateInfo.label}
                        {isActive && !awaitingSubmit && ` · ${upload.files_analyzed}/${total} pièces`}
                      </Typography>
                    </Box>
                    {isExpanded ? (
                      <KeyboardArrowUpIcon sx={{ fontSize: 18, color: tokens.color.textMuted }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ fontSize: 18, color: tokens.color.textMuted }} />
                    )}
                  </Box>

                  {/* État DONE : bloc célébration à la place des progress bars.
                      Iconographie verte + message "Demande finalisée" + CTAs
                      explicites. Pop-scale animé via animation CSS au mount. */}
                  {isDone ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 12px',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        border: `0.5px solid ${tokens.color.greenBorder}`,
                        borderRadius: '10px',
                        animation: `${popIn} 360ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: tokens.color.green,
                          color: tokens.color.white,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.32)',
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Urbanist, sans-serif',
                            fontSize: '13px',
                            fontWeight: 800,
                            color: tokens.color.greenDeep ?? tokens.color.greenDark,
                            lineHeight: 1.2,
                          }}
                        >
                          Demande finalisée
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Urbanist, sans-serif',
                            fontSize: '11.5px',
                            color: tokens.color.textSecondary ?? tokens.color.textMuted,
                            marginTop: '1px',
                            lineHeight: 1.3,
                          }}
                        >
                          {upload.registration_id
                            ? `Dossier #${upload.registration_id} créé — facture en cours d'émission.`
                            : 'Création OK — facture en cours.'}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Stack spacing={0.75}>
                      <ProgressRow
                        label="Téléversement"
                        pct={uploadPct}
                        count={`${upload.files_uploaded}/${total}`}
                        color={tokens.color.blue}
                        animated={isActive && !awaitingSubmit}
                      />
                      <ProgressRow
                        label="Analyse IA"
                        pct={analyzePct}
                        count={`${upload.files_analyzed}/${total}`}
                        color={tokens.color.yellow}
                        animated={isActive && !awaitingSubmit && upload.files_uploaded > 0}
                      />
                    </Stack>
                  )}

                  {/* Détails expandés : actions par dossier (typo legacy
                      pour cohérence avec les CTAs de l'étape 1). */}
                  {isExpanded && (
                    <ThemeProvider theme={legacyTheme}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ marginTop: '12px', paddingTop: '12px', borderTop: '0.5px solid rgba(0,0,0,0.06)' }}
                      >
                        <Tooltip
                          title={
                            finalizingLock && activeInFlightId !== upload.id
                              ? 'Finalisation en cours sur un autre dossier — impossible de basculer maintenant.'
                              : ''
                          }
                        >
                          <span style={{ flex: 1, display: 'flex' }}>
                            <Button
                              className="dealActionButton"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSeeUpload(upload);
                              }}
                              variant="contained"
                              color="primary"
                              disabled={finalizingLock && activeInFlightId !== upload.id}
                              startIcon={
                                isDone ? (
                                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                                ) : (
                                  <OpenInFullIcon sx={{ fontSize: 14 }} />
                                )
                              }
                              sx={{ flex: 1 }}
                            >
                              {isDone
                                ? 'Voir la demande terminée'
                                : awaitingSubmit
                                  ? 'Reprendre pour soumettre'
                                  : activeInFlightId === upload.id
                                    ? 'Voir le dossier'
                                    : 'Basculer sur ce dossier'}
                            </Button>
                          </span>
                        </Tooltip>
                        {isDone ? (
                          <Button
                            className="secondaryDealActionButton"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelUpload(upload.id);
                            }}
                          >
                            Fermer
                          </Button>
                        ) : (
                          <Button
                            className="secondaryDealActionButton"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelUpload(upload.id);
                            }}
                            disabled={upload.state === 'submitting'}
                          >
                            Annuler
                          </Button>
                        )}
                      </Stack>
                    </ThemeProvider>
                  )}

                  {/* Shimmer subtil quand actif — coupé une fois tout analysé
                      (plus rien ne "travaille", inutile de suggérer le contraire) */}
                  {isActive && !awaitingSubmit && !isExpanded && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '14px',
                        pointerEvents: 'none',
                        backgroundImage:
                          'linear-gradient(90deg, transparent, rgba(253, 209, 95, 0.08) 50%, transparent)',
                        backgroundSize: '200% 100%',
                        animation: `${shimmer} 3.2s ease-in-out infinite`,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>

          {/* ── Footer : action "Nouvelle CG" ──────────────────────────
              Typographie alignée sur le legacy theme (Urbanist) via
              `dealActionButton` — strictement même police/poids/transition
              que le CTA principal de l'étape 1 (PlateAndDepartmentScreen)
              et du recap. */}
          <Box
            sx={{
              padding: '12px 14px 16px',
              borderTop: '0.5px solid rgba(0, 0, 0, 0.06)',
            }}
          >
            <ThemeProvider theme={legacyTheme}>
              <Tooltip
                title={
                  finalizingLock
                    ? 'Finalisation en cours sur un dossier — patientez quelques secondes avant d\'en lancer un nouveau.'
                    : ''
                }
              >
                <span style={{ display: 'block' }}>
                  <Button
                    className="dealActionButton"
                    onClick={handleNewCg}
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={finalizingLock}
                    startIcon={<AddCircleOutlineIcon />}
                  >
                    Lancer une autre demande
                  </Button>
                </span>
              </Tooltip>
            </ThemeProvider>
          </Box>
        </>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
//  Helpers internes
// ─────────────────────────────────────────────────────────────

const ProgressRow = ({
  label,
  pct,
  count,
  color,
  animated,
}: {
  label: string;
  pct: number;
  count: string;
  color: string;
  animated: boolean;
}) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '3px' }}>
      <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: tokens.color.textMuted, letterSpacing: '0.02em' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '10.5px',
          fontWeight: 700,
          color: tokens.color.dark,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.02em',
        }}
      >
        {count} · {pct}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={pct}
      sx={{
        height: 4,
        borderRadius: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        '& .MuiLinearProgress-bar': {
          backgroundColor: color,
          borderRadius: 2,
          transition: animated ? 'transform 600ms ease' : 'transform 180ms ease',
        },
      }}
    />
  </Box>
);

